import React, { useState, useEffect } from 'react';
import { useUser, AVAILABLE_BADGES } from '../context/UserContext';
import { 
  User, Shield, Trophy, Clock, Calendar, Edit2, 
  Settings, Award, Activity, Footprints, Eye, Crown, 
  Sparkles, BookOpen, Moon, Save, LogOut, LogIn, Gavel,
  Flower, Zap, Wind, Coffee, Sunset, Flame, Droplets,
  Scissors, Compass, Heart, UserCheck, Layers, Scale, Sunrise,
  PauseCircle, Users
} from 'lucide-react';
import { db } from '../firebase';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { KoanHistoryEntry } from '../types';

const IconMap: Record<string, React.ReactNode> = {
  Footprints: <Footprints size={24} />,
  Eye: <Eye size={24} />,
  Crown: <Crown size={24} />,
  Sparkles: <Sparkles size={24} />,
  BookOpen: <BookOpen size={24} />,
  Moon: <Moon size={24} />,
  Flower: <Flower size={24} />,
  Zap: <Zap size={24} />,
  Gavel: <Gavel size={24} />,
  Wind: <Wind size={24} />,
  Coffee: <Coffee size={24} />,
  Sunset: <Sunset size={24} />,
  Flame: <Flame size={24} />,
  Droplets: <Droplets size={24} />,
  Scissors: <Scissors size={24} />,
  Compass: <Compass size={24} />,
  Heart: <Heart size={24} />,
  UserCheck: <UserCheck size={24} />,
  Layers: <Layers size={24} />,
  Scale: <Scale size={24} />,
  Sunrise: <Sunrise size={24} />,
  PauseCircle: <PauseCircle size={24} />,
  Users: <Users size={24} />,
};

const Profile: React.FC = () => {
  const { user, firebaseUser, updateName, resetProgress, login, logout, loading } = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState(user.name);
  const [activeTab, setActiveTab] = useState<'overview' | 'badges' | 'history' | 'zen' | 'settings'>('overview');
  const [koanHistory, setKoanHistory] = useState<KoanHistoryEntry[]>([]);
  const [loadingKoans, setLoadingKoans] = useState(false);

  const xpForNextLevel = 100 - (user.xp % 100);
  const progressPercent = (user.xp % 100);

  const handleSaveName = () => {
    if (newName.trim()) {
      updateName(newName);
      setIsEditing(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'zen' && user.uid) {
        const fetchKoans = async () => {
            setLoadingKoans(true);
            try {
                const q = query(collection(db, 'users', user.uid!, 'koan_history'), orderBy('timestamp', 'desc'), limit(20));
                const snapshot = await getDocs(q);
                const history: KoanHistoryEntry[] = [];
                snapshot.forEach(doc => {
                    history.push({ id: doc.id, ...doc.data() } as KoanHistoryEntry);
                });
                setKoanHistory(history);
            } catch (e) {
                console.error("Error fetching koan history", e);
            } finally {
                setLoadingKoans(false);
            }
        };
        fetchKoans();
    }
  }, [activeTab, user.uid]);

  const StatCard = ({ icon: Icon, label, value, color }: any) => (
    <div className="bg-white p-4 rounded-xl border border-nunchi-100 shadow-sm flex items-center gap-4">
      <div className={`p-3 rounded-full ${color}`}>
        <Icon size={20} />
      </div>
      <div>
        <div className="text-2xl font-bold text-nunchi-900">{value}</div>
        <div className="text-xs font-bold text-nunchi-400 uppercase tracking-widest">{label}</div>
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-6 pb-20 animate-fadeIn">
      {/* Profile Header */}
      <div className="relative mb-24">
        {/* Banner */}
        <div className="h-40 bg-gradient-to-r from-nunchi-600 to-nunchi-800 rounded-t-3xl relative overflow-hidden">
             <div className="absolute top-0 right-0 p-4">
                 {firebaseUser ? (
                    <div className="bg-black/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-400"></div>
                        Cloud Sync Active
                    </div>
                 ) : (
                    <div className="bg-black/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                        Guest Mode (Local Only)
                    </div>
                 )}
             </div>
        </div>
        
        {/* User Info Card */}
        <div className="absolute top-20 left-0 right-0 px-6">
          <div className="bg-white rounded-2xl shadow-lg border border-nunchi-200 p-6 flex flex-col md:flex-row items-center md:items-end gap-6">
            
            {/* Avatar */}
            <div className="relative -mt-16 md:-mt-12 flex-shrink-0">
               <div className="w-24 h-24 md:w-32 md:h-32 bg-nunchi-100 rounded-full border-4 border-white shadow-md flex items-center justify-center text-nunchi-400 overflow-hidden">
                 {firebaseUser?.photoURL ? (
                     <img src={firebaseUser.photoURL} alt="User" className="w-full h-full object-cover" />
                 ) : (
                     <User size={48} />
                 )}
               </div>
               <div className="absolute bottom-0 right-0 bg-amber-400 text-white text-xs font-bold px-2 py-1 rounded-full border-2 border-white shadow-sm">
                 Lvl {user.level}
               </div>
            </div>

            {/* Name & Title */}
            <div className="flex-1 text-center md:text-left">
               <div className="flex items-center justify-center md:justify-start gap-2 mb-1">
                 {isEditing ? (
                   <div className="flex items-center gap-2">
                     <input 
                       type="text" 
                       value={newName} 
                       onChange={(e) => setNewName(e.target.value)}
                       className="border border-nunchi-300 rounded px-2 py-1 text-xl font-bold text-nunchi-900 focus:ring-2 focus:ring-nunchi-500 outline-none"
                       autoFocus
                     />
                     <button onClick={handleSaveName} className="p-1.5 bg-green-500 text-white rounded-full hover:bg-green-600">
                       <Save size={16} />
                     </button>
                   </div>
                 ) : (
                   <>
                     <h1 className="text-2xl md:text-3xl font-serif font-bold text-nunchi-900">{user.name}</h1>
                     <button onClick={() => setIsEditing(true)} className="text-nunchi-400 hover:text-nunchi-600">
                       <Edit2 size={16} />
                     </button>
                   </>
                 )}
               </div>
               <p className="text-nunchi-500 font-medium">{firebaseUser ? firebaseUser.email : 'Guest User'}</p>
               <div className="flex items-center justify-center md:justify-start gap-2 text-xs text-nunchi-400 mt-2">
                 <Calendar size={12} /> Joined {new Date(user.joinedAt).toLocaleDateString()}
               </div>
            </div>

            {/* XP Progress */}
            <div className="w-full md:w-64">
               <div className="flex justify-between text-xs font-bold text-nunchi-500 mb-1">
                 <span>Level {user.level}</span>
                 <span>{user.xp} XP total</span>
               </div>
               <div className="w-full h-3 bg-nunchi-100 rounded-full overflow-hidden mb-1">
                 <div className="h-full bg-nunchi-600 transition-all duration-500" style={{ width: `${progressPercent}%` }}></div>
               </div>
               <div className="text-right text-[10px] text-nunchi-400">
                 {xpForNextLevel} XP to Level {user.level + 1}
               </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex justify-center border-b border-nunchi-200 mb-8 overflow-x-auto">
         <button 
           onClick={() => setActiveTab('overview')}
           className={`px-4 sm:px-6 py-3 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'overview' ? 'border-nunchi-800 text-nunchi-900' : 'border-transparent text-nunchi-400 hover:text-nunchi-600'}`}
         >
           Overview
         </button>
         <button 
           onClick={() => setActiveTab('badges')}
           className={`px-4 sm:px-6 py-3 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'badges' ? 'border-nunchi-800 text-nunchi-900' : 'border-transparent text-nunchi-400 hover:text-nunchi-600'}`}
         >
           Badges
         </button>
         <button 
           onClick={() => setActiveTab('history')}
           className={`px-4 sm:px-6 py-3 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'history' ? 'border-nunchi-800 text-nunchi-900' : 'border-transparent text-nunchi-400 hover:text-nunchi-600'}`}
         >
           History
         </button>
         <button 
           onClick={() => setActiveTab('zen')}
           className={`px-4 sm:px-6 py-3 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'zen' ? 'border-nunchi-800 text-nunchi-900' : 'border-transparent text-nunchi-400 hover:text-nunchi-600'}`}
         >
           Zen Journey
         </button>
         <button 
           onClick={() => setActiveTab('settings')}
           className={`px-4 sm:px-6 py-3 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'settings' ? 'border-nunchi-800 text-nunchi-900' : 'border-transparent text-nunchi-400 hover:text-nunchi-600'}`}
         >
           Settings
         </button>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'overview' && (
          <div className="animate-fadeIn space-y-8">
            <h2 className="text-lg font-bold text-nunchi-800 mb-4">Statistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
               <StatCard icon={User} label="Scenarios" value={user.scenariosCompleted} color="bg-blue-100 text-blue-600" />
               <StatCard icon={Shield} label="Assessments" value={user.assessmentsCompleted} color="bg-purple-100 text-purple-600" />
               <StatCard icon={Trophy} label="Badges" value={user.badges.length} color="bg-amber-100 text-amber-600" />
               <StatCard icon={Activity} label="Days Active" value="1" color="bg-emerald-100 text-emerald-600" />
            </div>
            
            <div className="bg-nunchi-50 rounded-2xl p-8 border border-nunchi-100">
               <h3 className="text-lg font-bold text-nunchi-800 mb-4">Current Focus</h3>
               <p className="text-nunchi-600 leading-relaxed">
                 You are currently at <strong>Level {user.level}</strong>. To advance quickly, focus on completing more scenarios in the Dojo and maintaining a high Nunchi score in assessments.
               </p>
            </div>
          </div>
        )}

        {activeTab === 'badges' && (
          <div className="animate-fadeIn">
            <h2 className="text-lg font-bold text-nunchi-800 mb-6">Achievements</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {AVAILABLE_BADGES.map((badge) => {
                const isUnlocked = user.badges.find(b => b.id === badge.id);
                return (
                  <div 
                    key={badge.id}
                    className={`relative p-6 rounded-xl border flex items-center gap-4 transition-all ${
                      isUnlocked 
                        ? 'bg-white border-nunchi-200 shadow-sm' 
                        : 'bg-gray-50 border-gray-100 opacity-60 grayscale'
                    }`}
                  >
                    <div className={`p-3 rounded-full flex-shrink-0 ${isUnlocked ? 'bg-amber-100 text-amber-600' : 'bg-gray-200 text-gray-400'}`}>
                       {IconMap[badge.icon] || <Award size={24} />}
                    </div>
                    <div>
                       <h3 className="font-bold text-nunchi-900">{badge.name}</h3>
                       <p className="text-xs text-nunchi-500 mt-1">{badge.description}</p>
                       {isUnlocked && (
                         <div className="text-[10px] text-green-600 font-bold mt-2 flex items-center gap-1">
                           <CheckCircleIcon size={10} /> Unlocked
                         </div>
                       )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="animate-fadeIn">
             <h2 className="text-lg font-bold text-nunchi-800 mb-6">Recent Activity</h2>
             <div className="bg-white rounded-xl border border-nunchi-200 overflow-hidden">
               {user.history.length === 0 ? (
                 <div className="p-8 text-center text-nunchi-400">No activity yet. Start your journey!</div>
               ) : (
                 <ul className="divide-y divide-nunchi-100">
                   {user.history.map((item, idx) => (
                     <li key={idx} className="p-4 hover:bg-nunchi-50 transition-colors flex justify-between items-center">
                       <div>
                         <p className="font-bold text-nunchi-800">{item.action}</p>
                         <p className="text-xs text-nunchi-400 flex items-center gap-1 mt-1">
                           <Clock size={10} /> {new Date(item.date).toLocaleDateString()} at {new Date(item.date).toLocaleTimeString()}
                         </p>
                       </div>
                       <div className="font-mono font-bold text-green-600 bg-green-50 px-2 py-1 rounded text-sm">
                         +{item.xpGained} XP
                       </div>
                     </li>
                   ))}
                 </ul>
               )}
             </div>
          </div>
        )}

        {activeTab === 'zen' && (
            <div className="animate-fadeIn">
                <h2 className="text-lg font-bold text-nunchi-800 mb-6 flex items-center gap-2">
                    <Gavel size={20} /> Koan Dojo History
                </h2>
                {loadingKoans ? (
                    <div className="text-center py-8 text-nunchi-500">Retrieving ancient scrolls...</div>
                ) : koanHistory.length === 0 ? (
                    <div className="bg-white rounded-xl p-8 text-center border border-nunchi-200">
                        <p className="text-nunchi-500">You have not attempted any Koans yet.</p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {koanHistory.map((entry) => (
                            <div key={entry.id} className="bg-white rounded-xl border border-nunchi-200 p-6 shadow-sm">
                                <div className="flex justify-between items-start mb-4">
                                    <h3 className="font-serif font-bold text-nunchi-900 text-lg">{entry.koanTitle}</h3>
                                    <span className={`px-2 py-1 rounded text-xs font-bold uppercase tracking-wider ${
                                        entry.masteryLevel === 'Master' ? 'bg-amber-100 text-amber-700' :
                                        entry.masteryLevel === 'Monk' ? 'bg-blue-100 text-blue-700' :
                                        'bg-gray-100 text-gray-600'
                                    }`}>
                                        {entry.masteryLevel}
                                    </span>
                                </div>
                                <div className="mb-4 bg-nunchi-50 p-3 rounded-lg text-sm text-nunchi-700 italic font-serif">
                                    "{entry.question}"
                                </div>
                                <div className="mb-4">
                                    <p className="text-xs text-nunchi-400 uppercase font-bold mb-1">Your Answer</p>
                                    <p className="text-nunchi-800">{entry.userAnswer}</p>
                                </div>
                                <div className="border-t border-nunchi-100 pt-4">
                                    <p className="text-xs text-nunchi-400 uppercase font-bold mb-1">Master's Feedback</p>
                                    <p className="text-nunchi-600 text-sm leading-relaxed">{entry.feedback}</p>
                                </div>
                                <div className="mt-4 text-xs text-nunchi-300 text-right">
                                    {new Date(entry.timestamp).toLocaleDateString()}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        )}

        {activeTab === 'settings' && (
          <div className="animate-fadeIn max-w-lg mx-auto">
             <div className="bg-white p-8 rounded-xl border border-nunchi-200 shadow-sm space-y-8">
                
                {/* Account Status */}
                <div>
                   <label className="block text-sm font-bold text-nunchi-600 mb-2">Account Status</label>
                   {!firebaseUser ? (
                       <div className="bg-orange-50 p-4 rounded-lg border border-orange-100">
                           <p className="text-orange-800 text-sm mb-3">You are currently in Guest Mode. Your progress is saved to this browser only. Sign in to sync your progress to the cloud.</p>
                           <button 
                             onClick={login}
                             className="bg-white text-nunchi-900 border border-nunchi-300 font-bold px-4 py-2 rounded-lg hover:bg-nunchi-50 shadow-sm flex items-center gap-2"
                           >
                             <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-4 h-4" alt="Google" />
                             Sign in with Google
                           </button>
                       </div>
                   ) : (
                       <div className="bg-green-50 p-4 rounded-lg border border-green-100 flex items-center justify-between">
                           <div>
                               <p className="text-green-800 font-bold text-sm">Signed in as {firebaseUser.email}</p>
                               <p className="text-green-600 text-xs">Progress is synced to the cloud.</p>
                           </div>
                           <button 
                             onClick={logout}
                             className="text-red-600 font-bold text-xs hover:bg-red-50 px-3 py-2 rounded-lg transition-colors flex items-center gap-1"
                           >
                             <LogOut size={14} /> Sign Out
                           </button>
                       </div>
                   )}
                </div>

                <div>
                  <label className="block text-sm font-bold text-nunchi-600 mb-2">Display Name</label>
                  <div className="flex gap-2">
                    <input 
                      type="text" 
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="flex-1 border border-nunchi-300 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-nunchi-200"
                    />
                    <button 
                      onClick={handleSaveName}
                      className="bg-nunchi-700 text-white font-bold px-4 py-2 rounded-lg hover:bg-nunchi-800"
                    >
                      Save
                    </button>
                  </div>
                </div>

                <div className="pt-8 border-t border-nunchi-100">
                   <h3 className="text-red-600 font-bold mb-2 flex items-center gap-2">
                     <Shield size={16} /> Danger Zone
                   </h3>
                   <p className="text-sm text-nunchi-500 mb-4">
                     This action will permanently delete your progress, badges, and history.
                   </p>
                   <button 
                     onClick={resetProgress}
                     className="w-full border border-red-200 text-red-600 font-bold py-3 rounded-lg hover:bg-red-50 transition-colors"
                   >
                     Reset All Progress
                   </button>
                </div>
             </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CheckCircleIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
);

export default Profile;